import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { WorkforceApiService } from '../workforce-api.service';
import { ProjectExpensePage } from '../workforce.types';

@Component({
  selector: 'app-project-expenses',
  standalone: false,
  templateUrl: './project-expenses.component.html',
  styles: [`
    .project-searchbar { display: flex; align-items: center; gap: 14px; max-width: 760px; min-height: 62px; margin: 20px 0 24px; padding: 0 16px 0 24px; border: 2px solid #60259e; border-radius: 36px; background: #f8f1fc; color: #60259e; }
    .project-searchbar input { flex: 1; min-width: 0; border: 0; outline: 0; background: transparent; color: #34354f; font: inherit; }
    .project-searchbar input::placeholder { color: #60259e; opacity: .85; }
    .project-searchbar button { display: grid; place-items: center; width: 42px; height: 42px; border: 0; border-radius: 50%; background: transparent; color: #60259e; cursor: pointer; }
    .project-searchbar button:hover, .project-searchbar button:focus-visible { background: #ecdcf6; }
    .project-filter-backdrop { position: fixed; inset: 0; z-index: 1000; display: grid; place-items: center; padding: 16px; background: #0005; }
    .project-filter-dialog { width: min(560px, 100%); padding: 24px; border-radius: 12px; background: #fff; box-shadow: 0 12px 40px #0003; color: #34354f; }
    .project-filter-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px; }
    .project-filter-header h2 { margin: 0; font-size: 18px; }
    .project-filter-header button { border: 0; background: transparent; color: #60259e; font-size: 26px; cursor: pointer; }
    .project-filter-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .project-filter-fields label { display: flex; flex-direction: column; gap: 6px; font-size: 13px; }
    .project-filter-fields input { min-height: 40px; padding: 6px 10px; border: 1px solid #d8cce6; border-radius: 6px; font: inherit; }
    .project-status-options { display: flex; flex-wrap: wrap; gap: 12px 22px; margin: 22px 0; }
    .project-status-options label { display: flex; align-items: center; gap: 7px; cursor: pointer; }
    .project-status-options input { accent-color: #60259e; }
    .project-filter-actions { display: flex; justify-content: flex-end; gap: 10px; }
    .project-filter-actions button, .project-pages button { min-height: 38px; padding: 6px 14px; border: 1px solid #60259e; border-radius: 6px; background: #fff; color: #60259e; cursor: pointer; }
    .project-filter-actions .primary { background: #60259e; color: #fff; }
    .project-pages { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin: 14px 0; }
    .project-pages nav { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .project-pages input { width: 68px; min-height: 38px; padding: 4px 8px; border: 1px solid #d8cce6; border-radius: 6px; }
    .project-pages select { min-height: 38px; border: 1px solid #d8cce6; border-radius: 6px; }
    .project-pages button:disabled, .project-filter-actions button:disabled { opacity: .45; cursor: default; }
    .project-expense-table { max-height: 520px; overflow: auto; }
    .project-expense-table th { position: sticky; top: 0; z-index: 1; }
    @media (max-width: 600px) { .project-filter-fields { grid-template-columns: 1fr; } .project-searchbar { min-height: 54px; } }
  `],
})
export class ProjectExpensesComponent implements OnInit, OnDestroy {
  private readonly api = inject(WorkforceApiService);
  private subscription?: Subscription;
  private searchTimer?: ReturnType<typeof setTimeout>;
  data: ProjectExpensePage | null = null;
  error = '';
  loading = false;
  startMonth = '';
  endMonth = '';
  search = '';
  status: 'all' | 'pending' | 'rejected' | 'approved' = 'all';
  draftStartMonth = '';
  draftEndMonth = '';
  draftStatus: 'all' | 'pending' | 'rejected' | 'approved' = 'all';
  filterOpen = false;
  page = 1;
  pageSize = 25;
  pageInput = 1;

  get invalidRange(): boolean {
    return Boolean(this.startMonth && this.endMonth && this.startMonth > this.endMonth);
  }

  ngOnInit(): void { this.load(); }
  ngOnDestroy(): void { this.subscription?.unsubscribe(); clearTimeout(this.searchTimer); }

  onSearchChange(): void {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.applyFilters(), 350);
  }

  openFilters(): void {
    this.draftStartMonth = this.startMonth;
    this.draftEndMonth = this.endMonth;
    this.draftStatus = this.status;
    this.filterOpen = true;
  }

  applyDraftFilters(): void {
    if (this.draftStartMonth && this.draftEndMonth && this.draftStartMonth > this.draftEndMonth) return;
    this.startMonth = this.draftStartMonth;
    this.endMonth = this.draftEndMonth;
    this.status = this.draftStatus;
    this.filterOpen = false;
    this.applyFilters();
  }

  applyFilters(): void {
    this.page = 1;
    this.load();
  }

  clearFilters(): void {
    this.startMonth = '';
    this.endMonth = '';
    this.search = '';
    this.status = 'all';
    this.filterOpen = false;
    this.applyFilters();
  }

  changePageSize(): void {
    this.page = 1;
    this.load();
  }

  jumpToPage(): void {
    if (!Number.isInteger(this.pageInput)) return;
    this.goToPage(this.pageInput);
  }

  goToPage(page: number): void {
    if (page < 1 || page > (this.data?.totalPages ?? 1) || page === this.page) return;
    this.page = page;
    this.load();
  }

  retry(): void { this.load(); }

  private load(): void {
    this.subscription?.unsubscribe();
    this.data = null;
    this.error = '';
    if (this.invalidRange) return;
    this.loading = true;
    this.subscription = this.api.get<ProjectExpensePage>('/workforce/project-expenses', {
      startMonth: this.startMonth || undefined,
      endMonth: this.endMonth || undefined,
      search: this.search.trim() || undefined,
      status: this.status === 'all' ? undefined : this.status,
      page: String(this.page),
      pageSize: String(this.pageSize),
    // Project records are imported independently, so always request fresh data.
    // This also prevents an older list remaining visible after a backend restart.
    }, true).subscribe({
      next: data => { this.data = data; this.pageInput = data.page; this.loading = false; },
      error: (error: HttpErrorResponse) => {
        const period = error.error?.period;
        this.error = error.error?.message || (Array.isArray(period) ? period[0] : period) || 'Unable to load project expenses.';
        this.loading = false;
      },
    });
  }
}
